/**
 * %CP editor fields: whole numbers only (no decimals in UI or payload).
 */

export function roundCpPercentStored(v) {
  if (v === '' || v == null) return '';
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  if (!Number.isFinite(n)) return '';
  return String(Math.round(Math.max(0, Math.min(400, n))));
}

/** Step stored %CP string by delta (used by graphic spinners). Empty → treat as 0 before stepping. */
export function bumpCpPercentString(raw, delta) {
  const cur = raw === '' || raw == null ? NaN : Number(String(raw).replace(',', '.'));
  const base = Number.isFinite(cur) ? cur : 0;
  return roundCpPercentStored(Math.round(base + delta));
}

/**
 * @param {string} raw — from <input>
 * @returns {string|undefined} next value, or undefined to keep previous (invalid input)
 */
export function nextCpPercentFromInput(raw) {
  const t = String(raw ?? '').trim();
  if (t === '') return '';
  const n = Math.round(parseFloat(t.replace(',', '.')));
  if (!Number.isFinite(n)) return undefined;
  return String(Math.max(0, Math.min(400, n)));
}

export function cpPercentToApiNumber(v) {
  if (v === '' || v == null) return null;
  const n = Math.round(Number(String(v).replace(',', '.')));
  return Number.isFinite(n) ? n : null;
}

/**
 * When %CP changes, shift cp_percent_min / cp_percent_max by the same delta so the range follows.
 * If only one bound is set and %CP was empty, min/max are left unchanged except cp_percent.
 */
export function patchCpPercentWithFollowingRange(seg, nextRaw) {
  const next = nextCpPercentFromInput(nextRaw);
  if (next === undefined) return null;
  if (next === '') {
    return { cp_percent: '' };
  }

  const newCenter = Number(next);
  const oldCenterNum = Number(String(seg.cp_percent ?? '').replace(',', '.'));
  const hasOldCenter = Number.isFinite(oldCenterNum) && String(seg.cp_percent ?? '').trim() !== '';

  const oldMin = seg.cp_percent_min === '' || seg.cp_percent_min == null ? NaN : Number(seg.cp_percent_min);
  const oldMax = seg.cp_percent_max === '' || seg.cp_percent_max == null ? NaN : Number(seg.cp_percent_max);
  const hasMin = Number.isFinite(oldMin);
  const hasMax = Number.isFinite(oldMax);

  if (!hasMin && !hasMax) {
    return { cp_percent: next };
  }

  let baseCenter;
  if (hasOldCenter) {
    baseCenter = oldCenterNum;
  } else if (hasMin && hasMax) {
    baseCenter = (oldMin + oldMax) / 2;
  } else {
    return { cp_percent: next };
  }

  const delta = newCenter - baseCenter;
  const patch = { cp_percent: next };
  if (hasMin) {
    patch.cp_percent_min = roundCpPercentStored(oldMin + delta);
  }
  if (hasMax) {
    patch.cp_percent_max = roundCpPercentStored(oldMax + delta);
  }
  const mn = patch.cp_percent_min != null ? Number(patch.cp_percent_min) : NaN;
  const mx = patch.cp_percent_max != null ? Number(patch.cp_percent_max) : NaN;
  if (Number.isFinite(mn) && Number.isFinite(mx) && mn > mx) {
    const tMin = patch.cp_percent_min;
    patch.cp_percent_min = patch.cp_percent_max;
    patch.cp_percent_max = tMin;
  }
  return patch;
}

/**
 * Graphic bar vertical drag: shift min/max by the same delta as %CP vs drag-start snapshot
 * (avoids range drifting when min/max are not updated between pointer-move frames).
 */
export function mergeCpPercentDragWithRangeShift(row, nextCp, snapshot) {
  const snapCenter = snapshot.center;
  const delta = nextCp - snapCenter;
  const cpStr = roundCpPercentStored(nextCp);
  const oldMin =
    snapshot.cp_percent_min === '' || snapshot.cp_percent_min == null
      ? NaN
      : Number(snapshot.cp_percent_min);
  const oldMax =
    snapshot.cp_percent_max === '' || snapshot.cp_percent_max == null
      ? NaN
      : Number(snapshot.cp_percent_max);
  const hasMin = Number.isFinite(oldMin);
  const hasMax = Number.isFinite(oldMax);
  if (!hasMin && !hasMax) {
    return { ...row, cp_percent: cpStr };
  }
  const out = { ...row, cp_percent: cpStr };
  if (hasMin) {
    out.cp_percent_min = roundCpPercentStored(oldMin + delta);
  }
  if (hasMax) {
    out.cp_percent_max = roundCpPercentStored(oldMax + delta);
  }
  const mn = out.cp_percent_min != null ? Number(out.cp_percent_min) : NaN;
  const mx = out.cp_percent_max != null ? Number(out.cp_percent_max) : NaN;
  if (Number.isFinite(mn) && Number.isFinite(mx) && mn > mx) {
    const tMin = out.cp_percent_min;
    out.cp_percent_min = out.cp_percent_max;
    out.cp_percent_max = tMin;
  }
  return out;
}
